import { FC } from 'react'
import mclasses from '../../styles/modal.module.css'
import Modal from '@common/Modal'
import { crmApi } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { IcoRemove } from '@/utils/icons'
import Button from '@UI/New/Button'
import { Lead, LeadState } from '@/api/types/crm/crm.types'
import { isError } from '@/helpers/checkQuery'
import { getLeadFieldValue } from '../../utils/leadFields'
import type { LeadsTableRow } from '../../utils/mapLeadsToTableRows'

type DeleteLeadModalTarget = Lead | LeadsTableRow

type LeadWithCurrentFunnel = (Lead & { currentFunnelId?: number | null }) | LeadsTableRow

interface DeleteLeadModalProps {
  keyTable: LeadState
  lead?: DeleteLeadModalTarget | DeleteLeadModalTarget[]
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  forceHardDelete?: boolean
  // onDismiss: () => void
}

const DeleteLeadModal: FC<DeleteLeadModalProps> = ({
  keyTable,
  lead,
  open,
  onSuccess,
  onClose,
  forceHardDelete = false,
}) => {
  const addAlert = useAddAlert()
  const isMultipleLeads = Array.isArray(lead)

  const handleConfirm = async () => {
    if (!lead) {
      addAlert({ text: 'Ошибка', type: 'error' })
      return
    }

    try {
      const selectedLeads: LeadWithCurrentFunnel[] = Array.isArray(lead)
        ? lead
        : lead
        ? [lead]
        : []
      const canRemoveByLeadFunnel = selectedLeads.every(
        (item) =>
          item.currentFunnelId !== null &&
          item.currentFunnelId !== undefined &&
          Number.isFinite(Number(item.currentFunnelId)),
      )

      let shouldCloseAfterAction = true

      if (!forceHardDelete && canRemoveByLeadFunnel) {
        const leadIdsByFunnelId = selectedLeads.reduce<Record<number, number[]>>(
          (acc, item) => {
            const funnelId = Number(item.currentFunnelId)
            if (!acc[funnelId]) acc[funnelId] = []
            acc[funnelId].push(item.id)
            return acc
          },
          {},
        )

        const responses = await Promise.all(
          Object.entries(leadIdsByFunnelId).map(([funnelId, leadIds]) =>
            crmApi.leads.bulkRemoveFunnelFromLeads({
              lead_ids: leadIds,
              funnel_id: Number(funnelId),
            }),
          ),
        )

        if (responses.some((res) => isError(res))) throw new Error()

        const summary = responses.reduce(
          (acc, res) => {
            const result = res.data?.data
            acc.removed += result?.removed_count ?? 0
            acc.skipped += result?.skipped_single_funnel_count ?? 0
            acc.notFound += result?.not_found_count ?? 0
            return acc
          },
          { removed: 0, skipped: 0, notFound: 0 },
        )

        const messageParts: string[] = []
        if (summary.removed > 0) {
          messageParts.push(`Удалено связок: ${summary.removed}`)
        }
        if (summary.skipped > 0) {
          messageParts.push(
            `Пропущено (нельзя удалить последнюю воронку): ${summary.skipped}`,
          )
        }
        if (summary.notFound > 0) {
          messageParts.push(`Не найдено связок: ${summary.notFound}`)
        }

        addAlert({
          title: summary.removed > 0 ? 'Успешно' : 'Без изменений',
          text:
            messageParts.length > 0
              ? messageParts.join('\n')
              : 'Изменений не произошло',
          type: summary.removed > 0 ? 'success' : 'error',
        })

        if (summary.removed === 0) {
          shouldCloseAfterAction = false
        }
      } else {
        const res = await crmApi.removeLeads(
          { type: keyTable },
          {
            lead_ids: selectedLeads.map((item) => item.id),
          },
        )

        if (isError(res)) throw new Error()
        addAlert({ title: 'Успешно', text: `Успешно удалено`, type: 'success' })
      }

      if (shouldCloseAfterAction) {
        onSuccess && onSuccess()
        onClose()
      }
    } catch (error) {
      addAlert({ title: 'Ошибка', text: 'Ошибка удаления', type: 'error' })
    }
  }

  const getSelectedNames = () => {
    if (!Array.isArray(lead)) return getLeadFieldValue(lead, 'NAME')
    return lead?.map((lead) => getLeadFieldValue(lead, 'NAME')).join(', ')
  }

  return (
    <Modal
      active={open}
      className={mclasses.modalWrapper as any}
      setActive={() => onClose()}
    >
      <div className={mclasses.modalContainer}>
        <div className={mclasses.modalHeader}>
          <div className={mclasses.modalTitleContainer}>
            <span className={mclasses.modalHeaderTitle}>
              {forceHardDelete
                ? isMultipleLeads
                  ? 'Вы уверены, что хотите удалить лидов и все их заявки?'
                  : 'Вы уверены, что хотите удалить лида и все его заявки?'
                : 'Вы уверены что хотите удалить?'}
            </span>
          </div>
          <div className={mclasses.closeIcon} onClick={() => onClose()}>
            <IcoRemove />
          </div>
        </div>
        <div className={mclasses.formWrapper}>
          <p>{getSelectedNames()}</p>
          {forceHardDelete && (
            <p>
              {isMultipleLeads
                ? 'Будут удалены лиды и все их заявки (заявки лидов с воронками).'
                : 'Будут удалены сам лид и все его заявки (заявки лида с воронками).'}
              Сами воронки CRM удалены не будут.
            </p>
          )}
        </div>
        <div className={mclasses.actions}>
          <Button color="gray" onClick={() => onClose()}>
            Отменить
          </Button>
          <Button color="primary" onClick={() => handleConfirm()}>
            Подтвердить
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default DeleteLeadModal
